'use client';
import React from 'react';
import dynamic from 'next/dynamic';
import { HistoryRecord } from './QRHistoryClient';

const QRCodeCanvas = dynamic(() => import('qrcode.react').then(m => m.QRCodeCanvas), { ssr: false });

const typeConfig: Record<string, { label: string; badgeClass: string }> = {
  url: { label: 'URL', badgeClass: 'badge-blue' },
  upi: { label: 'UPI', badgeClass: 'badge-orange' },
  whatsapp: { label: 'WhatsApp', badgeClass: 'badge-green' },
  wifi: { label: 'WiFi', badgeClass: 'badge-amber' },
  email: { label: 'Email', badgeClass: 'badge-violet' },
  phone: { label: 'Phone', badgeClass: 'badge-cyan' },
  maps: { label: 'Maps', badgeClass: 'badge-rose' },
  text: { label: 'Text', badgeClass: 'badge-slate' },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} · ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

interface HistoryTableProps {
  records: HistoryRecord[];
  selectedIds: Set<string>;
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (id: string, checked: boolean) => void;
  onDelete: (id: string) => void;
  onRedownload: (record: HistoryRecord) => void;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export default function HistoryTable({
  records,
  selectedIds,
  onSelectAll,
  onSelectRow,
  onDelete,
  onRedownload,
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onPageChange,
}: HistoryTableProps) {
  const allSelected = records.length > 0 && records.every(r => selectedIds.has(r.id));
  const someSelected = records.some(r => selectedIds.has(r.id));

  return (
    <div className="bg-card rounded-3xl border border-border shadow-card overflow-hidden">

      {/* Mobile card list — visible below md */}
      <div className="md:hidden">
        {/* Mobile select-all header */}
        <div className="px-4 py-3 border-b border-border flex items-center gap-3 bg-secondary/30">
          <input
            type="checkbox"
            checked={allSelected}
            ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
            onChange={e => onSelectAll(e.target.checked)}
            className="rounded border-border accent-primary w-5 h-5 cursor-pointer flex-shrink-0"
            aria-label="Select all"
          />
          <span className="text-muted-foreground font-semibold" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {someSelected ? `${selectedIds.size} selected` : 'Select All'}
          </span>
        </div>

        <div className="divide-y divide-border">
          {records.map(record => {
            const isSelected = selectedIds.has(record.id);
            const cfg = typeConfig[record.type] || { label: record.type, badgeClass: 'badge-slate' };
            return (
              <div
                key={record.id}
                className={`px-4 py-4 flex items-start gap-3 transition-colors ${isSelected ? 'bg-primary/4' : ''}`}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={e => onSelectRow(record.id, e.target.checked)}
                  className="rounded border-border accent-primary w-5 h-5 cursor-pointer flex-shrink-0 mt-0.5"
                  aria-label={`Select ${record.id}`}
                />

                {/* QR thumbnail */}
                <div
                  className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center border border-border flex-shrink-0"
                  style={{ background: record.options.bgColor || '#FFFFFF' }}
                >
                  <QRCodeCanvas
                    value={record.content}
                    size={44}
                    fgColor={record.options.fgColor}
                    bgColor={record.options.bgColor}
                    level={record.options.errorLevel as 'L' | 'M' | 'Q' | 'H'}
                    style={{ display: 'block', borderRadius: '4px' }}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`badge ${cfg.badgeClass}`}>{cfg.label}</span>
                    <span className="text-muted-foreground" style={{ fontSize: '11px' }}>{formatDateShort(record.createdAt)}</span>
                    <span className="text-muted-foreground" style={{ fontSize: '11px' }}>{record.options.size}px</span>
                  </div>
                  <p className="font-medium text-foreground truncate" style={{ fontSize: '13px' }} title={record.content}>
                    {record.content}
                  </p>
                  <div className="flex items-center gap-1">
                    <svg width="11" height="11" viewBox="0 0 13 13" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M6.5 8.5V2.5M6.5 8.5L4.5 6.5M6.5 8.5L8.5 6.5" />
                      <path d="M2 11h9" />
                    </svg>
                    <span className="text-muted-foreground" style={{ fontSize: '11px', fontVariantNumeric: 'tabular-nums' }}>{record.downloadCount} downloads</span>
                  </div>
                </div>

                {/* Actions — always visible on mobile */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => onRedownload(record)}
                    className="flex items-center justify-center rounded-xl text-primary bg-primary/8 hover:bg-primary/15 transition-colors"
                    style={{ width: '40px', height: '40px' }}
                    title="Re-download QR code"
                    aria-label="Re-download"
                  >
                    <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                      <path d="M7 9.5V2.5M7 9.5L5 7.5M7 9.5L9 7.5" />
                      <path d="M2 12h10" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDelete(record.id)}
                    className="flex items-center justify-center rounded-xl text-rose-500 bg-rose-50 hover:bg-rose-100 transition-colors"
                    style={{ width: '40px', height: '40px' }}
                    title="Delete this QR code record"
                    aria-label="Delete"
                  >
                    <svg width="15" height="15" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                      <path d="M2 4h10M5 4V2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V4M11.5 4l-.5 8a1 1 0 0 1-1 .9H4a1 1 0 0 1-1-.9L2.5 4" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop table — visible from md */}
      <div className="hidden md:block overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="px-5 py-3.5 text-left w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={e => onSelectAll(e.target.checked)}
                  className="rounded border-border accent-primary w-4 h-4 cursor-pointer"
                  aria-label="Select all"
                />
              </th>
              <th className="px-4 py-3.5 text-left" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>QR Preview</th>
              <th className="px-4 py-3.5 text-left" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Content</th>
              <th className="px-4 py-3.5 text-left" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Type</th>
              <th className="px-4 py-3.5 text-left" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Generated</th>
              <th className="px-4 py-3.5 text-left" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Downloads</th>
              <th className="px-4 py-3.5 text-left" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Size</th>
              <th className="px-5 py-3.5 text-right" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, idx) => {
              const isSelected = selectedIds.has(record.id);
              const cfg = typeConfig[record.type] || { label: record.type, badgeClass: 'badge-slate' };
              return (
                <tr
                  key={record.id}
                  className={`border-b border-border last:border-0 transition-colors duration-100 group ${
                    isSelected ? 'bg-primary/4' : idx % 2 === 0 ? 'bg-card' : 'bg-secondary/30'
                  } hover:bg-primary/5`}
                >
                  <td className="px-5 py-3.5">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={e => onSelectRow(record.id, e.target.checked)}
                      className="rounded border-border accent-primary w-4 h-4 cursor-pointer"
                      aria-label={`Select ${record.id}`}
                    />
                  </td>
                  <td className="px-4 py-3.5">
                    <div
                      className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center border border-border"
                      style={{ background: record.options.bgColor || '#FFFFFF' }}
                    >
                      <QRCodeCanvas
                        value={record.content}
                        size={44}
                        fgColor={record.options.fgColor}
                        bgColor={record.options.bgColor}
                        level={record.options.errorLevel as 'L' | 'M' | 'Q' | 'H'}
                        style={{ display: 'block', borderRadius: '4px' }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3.5 max-w-xs">
                    <p className="font-medium text-foreground truncate" style={{ fontSize: '13px', maxWidth: '220px' }} title={record.content}>
                      {record.content}
                    </p>
                    <p className="text-muted-foreground mt-0.5" style={{ fontSize: '11px' }}>
                      EC: {record.options.errorLevel} · {record.options.style}
                    </p>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`badge ${cfg.badgeClass}`}>{cfg.label}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <p className="text-foreground" style={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{formatDate(record.createdAt)}</p>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="var(--muted-foreground)" strokeWidth="1.5" strokeLinecap="round">
                        <path d="M6.5 8.5V2.5M6.5 8.5L4.5 6.5M6.5 8.5L8.5 6.5" />
                        <path d="M2 11h9" />
                      </svg>
                      <span className="font-semibold text-foreground" style={{ fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{record.downloadCount}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="text-muted-foreground" style={{ fontSize: '12px' }}>{record.options.size}px</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <button
                        onClick={() => onRedownload(record)}
                        className="p-2 rounded-xl text-primary hover:bg-primary/10 transition-colors"
                        title="Re-download QR code"
                        aria-label="Re-download"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                          <path d="M7 9.5V2.5M7 9.5L5 7.5M7 9.5L9 7.5" />
                          <path d="M2 12h10" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDelete(record.id)}
                        className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 transition-colors"
                        title="Delete this QR code record"
                        aria-label="Delete"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                          <path d="M2 4h10M5 4V2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 .5.5V4M11.5 4l-.5 8a1 1 0 0 1-1 .9H4a1 1 0 0 1-1-.9L2.5 4" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 sm:px-5 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-muted-foreground" style={{ fontSize: '13px' }}>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}–{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} QR codes
        </p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2.5 rounded-xl border border-border text-muted-foreground hover:border-primary/30 hover:text-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ minWidth: '40px', minHeight: '40px' }}
            aria-label="Previous page"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M9 3L5 7L9 11" />
            </svg>
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={`page-${page}`}
              onClick={() => onPageChange(page)}
              className={`rounded-xl font-semibold transition-all duration-150 ${
                currentPage === page
                  ? 'gradient-primary text-white shadow-blue'
                  : 'border border-border text-muted-foreground hover:border-primary/30 hover:text-primary'
              }`}
              style={{ fontSize: '13px', minWidth: '40px', minHeight: '40px' }}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2.5 rounded-xl border border-border text-muted-foreground hover:border-primary/30 hover:text-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ minWidth: '40px', minHeight: '40px' }}
            aria-label="Next page"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M5 3L9 7L5 11" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}